/**
 * Was eine Übung ist — Begriffe, nicht Rechnungen.
 *
 * Diese Listen sind die **eine** Quelle für drei Stellen, die sonst auseinander
 * laufen: die Postgres-Enums in `@kraftwerk/db`, die Zod-Prüfung an der
 * Sync-Grenze und die Auswahlfelder in der App. Eine vierte Schreibweise
 * irgendwo wäre ein Fehler, den erst ein abgelehnter Push sichtbar macht.
 */

/**
 * Wie ein Satz überhaupt aufgeschrieben wird.
 *
 * Das strukturell wichtigste Feld einer Übung: Es entscheidet, welche
 * Eingabefelder das Logbuch zeigt, ob ein geschätztes Maximum sinnvoll ist und
 * ob ein Volumen in Kilogramm überhaupt existiert. Ein Lauf hat kein Gewicht,
 * eine Planke keine Wiederholungen — ohne dieses Feld müsste beides so tun als
 * ob.
 */
export const TRACKING_MODES = [
  /** Gewicht × Wiederholungen. Der Normalfall an Hantel und Maschine. */
  'weightReps',
  /** Nur Wiederholungen; die Last ist das Körpergewicht (Klimmzug, Liegestütz). */
  'bodyweightReps',
  /** Körpergewicht plus Zusatzgewicht (Klimmzug mit Gürtel, Dips mit Hantel). */
  'weightedBodyweight',
  /** Gehaltene Zeit in Sekunden (Planke, Hang). Optional mit Zusatzgewicht. */
  'timeHold',
  /** Strecke und Dauer (Laufen). */
  'distanceTime',
] as const;

export type TrackingMode = (typeof TRACKING_MODES)[number];

export const EQUIPMENT = [
  'barbell',
  'dumbbell',
  'machine',
  'cable',
  'bodyweight',
  'kettlebell',
  'band',
  'other',
] as const;

export type Equipment = (typeof EQUIPMENT)[number];

/**
 * Muskelgruppen — grob genug, dass man sie beim Anlegen einer Übung wirklich
 * ankreuzt, fein genug für ein ehrliches Wochenvolumen.
 */
export const MUSCLES = [
  'chest',
  'back',
  'lats',
  'shoulders',
  'rearDelts',
  'biceps',
  'triceps',
  'forearms',
  'quads',
  'hamstrings',
  'glutes',
  'calves',
  'core',
  'lowerBack',
  'cardio',
] as const;

export type Muscle = (typeof MUSCLES)[number];

/**
 * Muskelgruppen für den Filter in der Übungssammlung (25.10.2026) — grob, wie
 * man im Studio denkt („heute Beine"). Hintere Schulter steht bei Schultern
 * und Rücken, unterer Rücken bei Rücken und Rumpf: Wer „Rücken" sucht, erwartet
 * beides.
 */
export const MUSCLE_GROUPS = {
  chest: ['chest'],
  back: ['back', 'lats', 'rearDelts', 'lowerBack'],
  shoulders: ['shoulders', 'rearDelts'],
  arms: ['biceps', 'triceps', 'forearms'],
  legs: ['quads', 'hamstrings', 'glutes', 'calves'],
  core: ['core', 'lowerBack'],
  cardio: ['cardio'],
} as const satisfies Record<string, readonly Muscle[]>;

export type MuscleGroup = keyof typeof MUSCLE_GROUPS;

/** Filter: eine ganze Gruppe oder ein einzelner Muskel; leer = alles. */
export type MuscleFilter = { group: MuscleGroup } | { muscle: Muscle } | null;

/**
 * Passt die Übung zum Filter? Gezählt werden nur die **Zielmuskeln** — sonst
 * stünde Bankdrücken unter Trizeps und jede Grundübung überall.
 */
export function matchesMuscleFilter(
  primaryMuscles: readonly Muscle[],
  filter: MuscleFilter,
): boolean {
  if (!filter) return true;
  const wanted: readonly Muscle[] =
    'group' in filter ? MUSCLE_GROUPS[filter.group] : [filter.muscle];
  return primaryMuscles.some((muscle) => wanted.includes(muscle));
}

/**
 * Bewegungsmuster. `isolation` ist bewusst dabei: Seitheben ist weder Drücken
 * noch Ziehen, und es in eine der beiden Schubladen zu zwingen würde die
 * Balance-Anzeige („diese Woche 12 Sätze Drücken, 4 Sätze Ziehen") verfälschen.
 */
export const MOVEMENT_PATTERNS = [
  'push',
  'pull',
  'squat',
  'hinge',
  'lunge',
  'core',
  'carry',
  'cardio',
  'isolation',
] as const;

export type MovementPattern = (typeof MOVEMENT_PATTERNS)[number];

export const BODY_REGIONS = ['upper', 'lower', 'core', 'fullBody'] as const;

export type BodyRegion = (typeof BODY_REGIONS)[number];

/**
 * Ein hinterlegtes Video.
 *
 * Nur Verweis, nie eingebettet: Ein Player von einer fremden Seite lädt bei
 * jedem Öffnen Fremdcode in eine App voller Gesundheitsdaten — und zeigt im
 * Funkloch einen weißen Kasten statt einer Erklärung.
 */
export interface ExerciseVideo {
  label: string;
  url: string;
  /** Sekunde, bei der die Erklärung anfängt. `null` = von vorn. */
  startSeconds?: number | null;
}

/** Erlaubt sind ausschließlich `https`-Adressen. */
export function isSafeVideoUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Der Gastgeber einer Videoadresse, für die Anzeige am Knopf.
 *
 * Der Nutzer soll sehen, wohin er die App schickt, bevor er tippt — „Video
 * ansehen" allein verrät das nicht.
 */
export function videoHost(value: string): string | null {
  try {
    return new URL(value).host.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * Adresse mit Startzeit, falls eine hinterlegt ist.
 *
 * YouTube und die meisten anderen verstehen `?t=` in Sekunden; wo nicht, landet
 * man am Anfang — schlimmstenfalls also dort, wo man ohne das Feld ohnehin wäre.
 */
export function videoUrlWithStart(video: ExerciseVideo): string {
  if (!video.startSeconds || video.startSeconds <= 0) return video.url;
  try {
    const url = new URL(video.url);
    url.searchParams.set('t', String(Math.floor(video.startSeconds)));
    return url.toString();
  } catch {
    return video.url;
  }
}

/** Welche Eingabefelder ein Satz in dieser Aufzeichnungsart braucht. */
export interface SetFields {
  weight: boolean;
  reps: boolean;
  seconds: boolean;
  distance: boolean;
  /** Braucht das Körpergewicht, um Last und Maximum ausrechnen zu können. */
  bodyWeight: boolean;
}

export function setFieldsFor(mode: TrackingMode): SetFields {
  switch (mode) {
    case 'weightReps':
      return { weight: true, reps: true, seconds: false, distance: false, bodyWeight: false };
    case 'bodyweightReps':
      return { weight: false, reps: true, seconds: false, distance: false, bodyWeight: true };
    case 'weightedBodyweight':
      return { weight: true, reps: true, seconds: false, distance: false, bodyWeight: true };
    case 'timeHold':
      return { weight: true, reps: false, seconds: true, distance: false, bodyWeight: false };
    case 'distanceTime':
      return { weight: false, reps: false, seconds: true, distance: true, bodyWeight: false };
  }
}
